import React, { useState, useRef, useEffect } from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import GetUserInfoService from "../../../../../service/GetUserInfoService";
import "./Notifications.css";

const getToken = () => localStorage.getItem("__extraconomy_token");
const getUsername = () => localStorage.getItem("__extraconomy_username");

const mapApiNotificationToUi = (notification, index) => {
  const id = notification.id ?? index;
  const title = notification.titleDeviceMessage ?? notification.title ?? "";
  const message = notification.deviceMessage ?? notification.message ?? "";
  const createdAt =
    notification.dateNotification ||
    notification.createdAt ||
    notification.date ||
    new Date().toISOString();

  return {
    id,
    title,
    message,
    icon: "pi pi-bell",
    date: createdAt,
    read: Boolean(notification.sent),
  };
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const op = useRef(null);
  const serviceRef = useRef(new GetUserInfoService());

  useEffect(() => {
    const loadNotifications = async () => {
      const token = getToken();
      const username = getUsername();
      if (!token || !username) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await serviceRef.current.getUserNotifications(token, username);
        const items = Array.isArray(response?.result) ? response.result : (response ?? []);
        const mapped = items.map(mapApiNotificationToUi);
        setNotifications(mapped);
        setUnreadCount(mapped.filter((n) => !n.read).length);
      } catch (error) {
        console.error("Failed to load notifications:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadNotifications();
  }, []);

  const handleNotificationClick = async (notification) => {
    if (notification.read) return;

    const token = getToken();
    const username = getUsername();

    if (!token || !username) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      return;
    }

    try {
      await serviceRef.current.postUserNotifications(token, username, notification.id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    if (!notifications.length) return;

    const token = getToken();
    const username = getUsername();

    const unread = notifications.filter((n) => !n.read);
    if (!unread.length) return;

    if (!token || !username) {
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      return;
    }

    try {
      await Promise.all(
        unread.map((n) => serviceRef.current.postUserNotifications(token, username, n.id)),
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Agora";
    if (minutes < 60) return `${minutes}min`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  return (
    <div className="notifications-container">
      <Button
        icon="pi pi-bell"
        className="p-button-text p-button-rounded notifications-button p-no-outline"
        onClick={(e) => op.current.toggle(e)}
        aria-label="Notificações"
        style={{ color: "var(--theme-text, #171717)" }}
      >
        {unreadCount > 0 && (
          <Badge value={unreadCount > 99 ? "99+" : unreadCount} severity="danger" />
        )}
      </Button>

      <OverlayPanel
        ref={op}
        className="notifications-overlay"
        dismissable
        style={{ backgroundColor: "var(--theme-background, #FFFFFF)" }}
      >
        <div className="notifications-header">
          <h5 className="m-0" style={{ color: "var(--theme-text, #171717)" }}>
            Notificações
          </h5>
          {unreadCount > 0 && (
            <Button
              label="Marcar todas como lidas"
              className="p-button-text p-button-sm"
              onClick={markAllAsRead}
              style={{ color: "var(--theme-text, #171717)" }}
            />
          )}
        </div>

        <div className="notifications-list">
          {isLoading ? (
            <div
              className="notifications-empty"
              style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
            >
              Carregando...
            </div>
          ) : notifications.length === 0 ? (
            <div
              className="notifications-empty"
              style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
            >
              Nenhuma notificação
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`notifications-item ${!notification.read ? "notifications-item-unread" : ""}`}
                onClick={() => handleNotificationClick(notification)}
                style={{ backgroundColor: "var(--theme-background, #FFFFFF)" }}
              >
                <div className="notifications-item-icon">
                  <i className={notification.icon}></i>
                </div>
                <div className="notifications-item-content">
                  <div
                    className="notifications-item-title"
                    style={{ color: "var(--theme-text, #171717)" }}
                  >
                    {notification.title}
                  </div>
                  <div
                    className="notifications-item-message"
                    style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                  >
                    {notification.message}
                  </div>
                  <div
                    className="notifications-item-time"
                    style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
                  >
                    {formatTimeAgo(notification.date)}
                  </div>
                </div>
                {!notification.read && <div className="notifications-item-dot"></div>}
              </div>
            ))
          )}
        </div>
      </OverlayPanel>
    </div>
  );
};

export default React.memo(Notifications);
